export const POLICY_LINKS = [
  { slug: "privacy-policy", label: "Privacy Policy" },
  { slug: "terms-and-conditions", label: "Terms & Conditions" },
  { slug: "returns-refunds", label: "Returns & Refunds" },
  { slug: "credit-application", label: "Credit Application" },
  { slug: "delivery-policy", label: "Delivery Policy" },
  { slug: "price-match-policy", label: "Price-Match Policy" },
];

export const POLICIES = {
  "privacy-policy": {
    title: "Privacy Policy",
    lead: "How Brampton Cash & Carry handles business contact information and customer data connected to this website and the BlueFig ordering portal.",
    sections: [
      {
        heading: "Information we handle",
        paragraphs: [
          "This website is a static marketing website. It does not store customer accounts, passwords, orders or payment details.",
          "If you contact us by phone, email, WhatsApp or Google Maps, we may receive the business contact details you choose to share. Customer registration, ordering, invoices and profile information are handled inside BlueFig.",
        ],
        bullets: ["Business name and contact details", "Product, delivery or account enquiries", "BlueFig account and ordering information managed by BlueFig", "Standard technical logs that may be processed by the website hosting provider"],
      },
      {
        heading: "How information is used",
        paragraphs: ["Information is used to respond to enquiries, discuss wholesale supply, support customer accounts and help operate the ordering relationship."],
        bullets: ["Respond to phone, email or WhatsApp enquiries", "Support BlueFig account setup and customer service", "Review product, delivery, credit or price-match requests", "Meet legal, accounting and business record requirements"],
      },
      {
        heading: "BlueFig and service providers",
        paragraphs: [
          "Customer login, profiles, ordering and account data are provided through BlueFig. BlueFig acts as a separate service provider and may have its own privacy terms.",
          "We may also use website hosting, email, telecommunications and map service providers to operate this website and communicate with customers.",
        ],
      },
      {
        heading: "Retention and security",
        paragraphs: [
          "Business records are kept only as long as reasonably needed for customer service, accounting, legal or operational purposes. Reasonable administrative and technical safeguards are used, but no internet service can be guaranteed to be completely secure.",
        ],
      },
      {
        heading: "Your choices and questions",
        paragraphs: [
          "You may contact us to ask about the business contact information we hold, request a correction or ask about deletion where legally permitted. BlueFig account information may also need to be managed through your BlueFig profile.",
        ],
      },
    ],
  },
  "terms-and-conditions": {
    title: "Terms & Conditions",
    lead: "The general terms for using this website and understanding how wholesale ordering is handled through BlueFig.",
    sections: [
      {
        heading: "Website information",
        paragraphs: [
          "This website provides general information about Brampton Cash & Carry, its product categories, delivery service and ordering process. Content may change without notice and does not guarantee product availability or final pricing.",
        ],
      },
      {
        heading: "BlueFig accounts and orders",
        paragraphs: [
          "Customer login, registration, profiles, online orders and account-specific information are managed inside BlueFig. Your use of BlueFig may be subject to BlueFig's own terms and policies.",
          "An order is not final until it has been accepted and confirmed through the applicable ordering and fulfilment process.",
        ],
      },
      {
        heading: "Products, pricing and availability",
        paragraphs: [
          "Product descriptions, pack sizes, photographs, prices and availability may change. Customer-specific pricing, taxes, deposits, delivery fees or minimums may apply and will be shown or confirmed through the ordering process where applicable.",
        ],
      },
      {
        heading: "Acceptable website use",
        paragraphs: ["You agree not to misuse this website, interfere with its operation, attempt unauthorized access, or use its content in a misleading or unlawful way."],
      },
      {
        heading: "Liability and governing law",
        paragraphs: [
          "The website is provided on an as-available basis. To the extent permitted by law, Brampton Cash & Carry is not liable for indirect or consequential losses arising from website use. These terms are governed by the laws of Alberta and the applicable laws of Canada.",
        ],
      },
    ],
  },
  "returns-refunds": {
    title: "Returns & Refunds",
    lead: "How damaged, incorrect or unsuitable wholesale products are reviewed for return, replacement or credit.",
    sections: [
      {
        heading: "Check your delivery",
        paragraphs: ["Please inspect deliveries when they arrive and report missing, damaged or incorrect items as soon as possible. Delivery documents and product details help our team review the issue quickly."],
      },
      {
        heading: "Perishable products",
        paragraphs: [
          "Fresh, frozen, refrigerated and other perishable products may not be returnable once accepted unless they were damaged, incorrect or failed to meet applicable quality requirements at delivery.",
        ],
      },
      {
        heading: "Non-perishable products",
        paragraphs: [
          "Unopened and resalable non-perishable products may be reviewed for return or credit. Products that have been opened, used, stored improperly or specially ordered may not be eligible.",
        ],
      },
      {
        heading: "Refund or credit method",
        paragraphs: [
          "Approved claims may be resolved by replacement, account credit or refund to the original payment method. The resolution depends on the product, claim, supplier requirements and account status.",
        ],
      },
      {
        heading: "How to request a review",
        paragraphs: ["Contact our team with your business name, invoice or order reference, product details and photos where helpful. Do not dispose of affected products until the claim has been reviewed."],
      },
    ],
  },
  "credit-application": {
    title: "Credit Application",
    lead: "How wholesale customer accounts and requests for payment terms are reviewed.",
    portalCta: true,
    sections: [
      {
        heading: "How to apply",
        paragraphs: [
          "New customers can begin through the BlueFig Login / Sign Up page or by contacting our team. Credit accounts and payment terms are not automatic and require review and approval.",
        ],
      },
      {
        heading: "Information that may be requested",
        paragraphs: ["A credit review may require current business and payment information."],
        bullets: ["Legal and operating business name", "Business address and delivery address", "Owner or authorized contact details", "Tax or business registration information", "Trade or supplier references", "Requested payment terms or credit limit"],
      },
      {
        heading: "Approval and account terms",
        paragraphs: [
          "Approved credit limits, due dates and payment methods are confirmed separately. Until credit is approved, prepayment or payment at ordering may be required.",
        ],
      },
      {
        heading: "Ongoing account conduct",
        paragraphs: [
          "Credit terms may be changed, suspended or withdrawn if invoices become overdue, information changes materially, or account activity presents unacceptable risk. Additional charges may apply where permitted by law or confirmed account terms.",
        ],
      },
      {
        heading: "Data handling",
        paragraphs: ["Credit information is used to evaluate and manage the wholesale account. Customer profile and portal information remain connected to BlueFig rather than this static website."],
      },
    ],
  },
  "delivery-policy": {
    title: "Delivery Policy",
    lead: "General delivery, receiving and service-area information for wholesale orders.",
    sections: [
      {
        heading: "Service area and scheduling",
        paragraphs: [
          "Delivery availability depends on the delivery address, order size, product type and current route schedule. Edmonton and Alberta delivery options may be shown during ordering or confirmed by our team.",
        ],
      },
      {
        heading: "Order cut-off times",
        paragraphs: [
          "Cut-off times, minimum order amounts and delivery days can vary by area and demand. The applicable details will be shown at checkout or confirmed before fulfilment.",
        ],
      },
      {
        heading: "Delivery charges and minimums",
        paragraphs: [
          "Free delivery thresholds, delivery fees, fuel charges, minimums and special handling charges may apply. Applicable charges should be reviewed before an order is submitted or confirmed.",
        ],
      },
      {
        heading: "Receiving requirements",
        paragraphs: [
          "A responsible person should be available to receive, inspect and store the delivery. Safe access, accurate delivery instructions and suitable storage are required, especially for refrigerated or frozen products.",
        ],
      },
      {
        heading: "Delays and missed deliveries",
        paragraphs: [
          "Weather, traffic, supplier availability and operational issues may affect delivery times. If a delivery cannot be completed because access or receiving arrangements are unavailable, re-delivery or additional charges may apply.",
        ],
      },
    ],
  },
  "price-match-policy": {
    title: "Price-Match Policy",
    lead: "How comparable supplier prices are reviewed for a possible match or better offer.",
    sections: [
      {
        heading: "Eligible requests",
        paragraphs: [
          "Price-match reviews require a recent supplier invoice, quotation or written price list. Products must be comparable in brand, specification, pack size, quantity, condition and delivery requirements.",
        ],
      },
      {
        heading: "Verification",
        paragraphs: [
          "Our team may verify the competing price, product availability, supplier terms, taxes, deposits, delivery fees and order minimums before confirming eligibility.",
        ],
      },
      {
        heading: "Exclusions",
        paragraphs: ["A price match may not apply in every situation."],
        bullets: ["Clearance, liquidation or closeout pricing", "Limited-time promotions or membership-only offers", "Out-of-stock or unavailable competitor products", "Different brands, pack sizes, specifications or quantities", "Pricing errors, damaged goods or non-comparable delivery terms"],
      },
      {
        heading: "Final approval",
        paragraphs: [
          "A request is not guaranteed. Any match or better offer is subject to availability, margin, account status and final approval by Brampton Cash & Carry.",
        ],
      },
      {
        heading: "How to request a review",
        paragraphs: ["Call or email our team with a recent invoice or quotation, the products you regularly purchase and your delivery location."],
      },
    ],
  },
};
